/**
 * RGPDZipSharesAuditPanel — Dashboard Super Admin pour les partages ZIP RGPD.
 *
 * Conformité :
 *  - Article 30 : registre complet des partages (zip_shares) avec filtres
 *  - Article 17 : déclenchement manuel de la politique de rétention
 *  - Article 32 : visualisation des access logs (qui a téléchargé, IP, UA)
 *
 * Endpoints backend :
 *  - GET  /api/ged/rgpd/audit                (liste paginée + stats)
 *  - GET  /api/ged/rgpd/audit/{share_id}/access-logs (détail accès d'un ZIP)
 *  - GET  /api/ged/rgpd/admin/retention/status (statut scheduler)
 *  - POST /api/ged/rgpd/admin/retention/run?dry_run=...&days=... (exécution manuelle)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, FileArchive, AlertTriangle, Search, Eye, Clock, Calendar,
  CheckCircle2, XCircle, Loader2, Trash2, RefreshCw, Download, ChevronRight,
  Globe, User as UserIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getToken } from '../services/tokenService';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUT_META = {
  actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  revoque: { label: 'Révoqué', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: XCircle },
  anonymise: { label: 'Anonymisé (Art. 17)', color: 'bg-slate-100 text-slate-600 border-slate-300', icon: Shield },
};

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateFR(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function RGPDZipSharesAuditPanel() {
  const [shares, setShares] = useState([]);
  const [stats, setStats] = useState({ total_actifs: 0, total_revoques: 0, total_anonymises: 0 });
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [statutFilter, setStatutFilter] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [cooperEmail, setCooperEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Détail logs
  const [selectedShare, setSelectedShare] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Politique rétention
  const [retentionStatus, setRetentionStatus] = useState(null);
  const [retentionDays, setRetentionDays] = useState(365);
  const [retentionPreview, setRetentionPreview] = useState(null);
  const [retentionRunning, setRetentionRunning] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const loadAudit = useCallback(async (skipOverride = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('skip', String(skipOverride ?? skip));
      if (statutFilter) params.set('statut', statutFilter);
      if (memberEmail) params.set('member_email', memberEmail);
      if (cooperEmail) params.set('cooper_email', cooperEmail);
      const res = await axios.get(`${API}/ged/rgpd/audit?${params}`, { headers: headers() });
      setShares(res.data.shares || []);
      setStats(res.data.stats || { total_actifs: 0, total_revoques: 0, total_anonymises: 0 });
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger le registre RGPD');
    } finally {
      setLoading(false);
    }
  }, [headers, limit, skip, statutFilter, memberEmail, cooperEmail]);

  const loadRetentionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ged/rgpd/admin/retention/status`, { headers: headers() });
      setRetentionStatus(res.data);
    } catch {
      setRetentionStatus({ enabled: false });
    }
  }, [headers]);

  useEffect(() => {
    loadAudit();
    loadRetentionStatus();
  }, []); // eslint-disable-line

  const openShareLogs = async (share) => {
    setSelectedShare(share);
    setLogsLoading(true);
    setAccessLogs([]);
    try {
      const res = await axios.get(`${API}/ged/rgpd/audit/${encodeURIComponent(share.id)}/access-logs`, { headers: headers() });
      setAccessLogs(res.data.logs || []);
    } catch {
      toast.error('Impossible de charger les logs d\'accès');
    } finally {
      setLogsLoading(false);
    }
  };

  const previewRetention = async () => {
    setRetentionRunning(true);
    setRetentionPreview(null);
    try {
      const res = await axios.post(
        `${API}/ged/rgpd/admin/retention/run?dry_run=true&days=${retentionDays}`,
        null,
        { headers: headers() },
      );
      setRetentionPreview(res.data);
      toast.info(`Aperçu : ${res.data.would_delete_zip_files} ZIP à supprimer, ${res.data.would_anonymize_access_logs} logs à anonymiser`);
    } catch {
      toast.error('Erreur lors de l\'aperçu');
    } finally {
      setRetentionRunning(false);
    }
  };

  const runRetention = async () => {
    if (!retentionPreview) {
      toast.error('Faites d\'abord un aperçu (dry-run) avant d\'exécuter');
      return;
    }
    const total = (retentionPreview.would_delete_zip_files || 0) + (retentionPreview.would_anonymize_access_logs || 0);
    if (total === 0) {
      toast.info('Rien à purger');
      return;
    }
    if (!window.confirm(`Confirmer : supprimer ${retentionPreview.would_delete_zip_files} ZIP physiques et anonymiser ${retentionPreview.would_anonymize_access_logs} logs ?\n\nCette action est IRRÉVERSIBLE.`)) return;
    setRetentionRunning(true);
    try {
      const res = await axios.post(
        `${API}/ged/rgpd/admin/retention/run?dry_run=false&days=${retentionDays}`,
        null,
        { headers: headers() },
      );
      toast.success(`Effacement effectué : ${res.data.deleted_zip_files} ZIP, ${res.data.anonymized_access_logs} logs`);
      setRetentionPreview(null);
      await Promise.all([loadAudit(), loadRetentionStatus()]);
    } catch {
      toast.error('Erreur lors de l\'exécution');
    } finally {
      setRetentionRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rgpd-zip-shares-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-950 to-slate-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <Shield className="h-6 w-6 text-indigo-200" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Audit RGPD — Partages ZIP</h2>
              <p className="text-sm text-indigo-200 mt-1">
                Registre des transferts E2E conforme aux Articles 15, 17, 30 & 32 du RGPD.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadAudit(); loadRetentionStatus(); }}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            data-testid="rgpd-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rafraîchir
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
            <div className="text-xs uppercase tracking-wider text-emerald-300">Actifs</div>
            <div className="text-2xl font-bold mt-1" data-testid="rgpd-kpi-actifs">{stats.total_actifs}</div>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
            <div className="text-xs uppercase tracking-wider text-amber-300">Révoqués</div>
            <div className="text-2xl font-bold mt-1" data-testid="rgpd-kpi-revoques">{stats.total_revoques}</div>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
            <div className="text-xs uppercase tracking-wider text-slate-300">Anonymisés (Art. 17)</div>
            <div className="text-2xl font-bold mt-1" data-testid="rgpd-kpi-anonymises">{stats.total_anonymises}</div>
          </div>
        </div>
      </div>

      {/* Politique de rétention */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-indigo-600" />
              Politique de rétention (Article 17 - Droit à l'effacement)
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Effacement automatique des ZIP physiques des partages révoqués depuis plus de N jours + anonymisation des logs.
            </p>
          </div>
          {retentionStatus?.running ? (
            <Badge className="bg-emerald-700 text-white border-emerald-900 font-semibold" data-testid="retention-scheduler-status">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Scheduler actif
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-500" data-testid="retention-scheduler-status">
              <AlertTriangle className="h-3 w-3 mr-1" /> Inactif
            </Badge>
          )}
        </div>

        {retentionStatus && (
          <div className="text-xs text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 bg-slate-50 rounded p-3">
            <div><span className="text-slate-400">Fréquence :</span> quotidien à {retentionStatus.schedule || '—'}</div>
            <div><span className="text-slate-400">Seuil :</span> {retentionStatus.retention_days || 365} jours</div>
            <div><span className="text-slate-400">Dernière exéc :</span> {formatDateFR(retentionStatus.last_run)}</div>
            <div><span className="text-slate-400">Prochaine :</span> {formatDateFR(retentionStatus.next_run)}</div>
          </div>
        )}

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label htmlFor="retention-days" className="text-xs">Conserver les révoqués (jours)</Label>
            <Input
              id="retention-days"
              type="number"
              min="30"
              max="3650"
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value) || 365)}
              className="h-9 w-32"
              data-testid="retention-days-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={previewRetention}
            disabled={retentionRunning}
            data-testid="retention-preview-btn"
          >
            {retentionRunning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
            Aperçu (dry-run)
          </Button>
          <Button
            size="sm"
            onClick={runRetention}
            disabled={retentionRunning || !retentionPreview || ((retentionPreview.would_delete_zip_files || 0) + (retentionPreview.would_anonymize_access_logs || 0) === 0)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="retention-run-btn"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Exécuter l'effacement
          </Button>
        </div>

        {retentionPreview && (
          <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="retention-preview-result">
            <div className="font-semibold mb-1">Aperçu (rien n'a été supprimé)</div>
            <div>• {retentionPreview.would_delete_zip_files} fichiers ZIP physiques à supprimer (cutoff: {formatDateFR(retentionPreview.cutoff)})</div>
            <div>• {retentionPreview.would_anonymize_access_logs} access logs à anonymiser</div>
          </div>
        )}
      </div>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Statut</Label>
            <select
              value={statutFilter}
              onChange={(e) => { setStatutFilter(e.target.value); setSkip(0); }}
              className="w-full h-9 text-sm border border-slate-300 rounded px-2"
              data-testid="rgpd-filter-statut"
            >
              <option value="">Tous</option>
              <option value="actif">Actifs</option>
              <option value="revoque">Révoqués</option>
              <option value="anonymise">Anonymisés</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Email expéditeur (member)</Label>
            <Input
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              placeholder="membre@..."
              className="h-9"
              data-testid="rgpd-filter-member-email"
            />
          </div>
          <div>
            <Label className="text-xs">Email destinataire (cooper)</Label>
            <Input
              value={cooperEmail}
              onChange={(e) => setCooperEmail(e.target.value)}
              placeholder="cooper@..."
              className="h-9"
              data-testid="rgpd-filter-cooper-email"
            />
          </div>
          <div className="flex items-end">
            <Button
              size="sm"
              onClick={() => { setSkip(0); loadAudit(0); }}
              className="h-9 w-full"
              data-testid="rgpd-search-btn"
            >
              <Search className="h-3.5 w-3.5 mr-1" /> Rechercher
            </Button>
          </div>
        </div>
      </div>

      {/* Liste des partages */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            <FileArchive className="inline h-4 w-4 mr-1 text-indigo-600" />
            {total} partage{total > 1 ? 's' : ''} dans le registre
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 inline" /></div>
        ) : shares.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Aucun partage trouvé</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {shares.map((s) => {
              const meta = STATUT_META[s.statut] || STATUT_META.actif;
              const StatusIcon = meta.icon;
              return (
                <div key={s.id} className="p-4 hover:bg-slate-50 flex items-start gap-4" data-testid={`rgpd-share-row-${s.id}`}>
                  <div className="shrink-0">
                    <Badge variant="outline" className={`${meta.color} flex items-center gap-1`}>
                      <StatusIcon className="h-3 w-3" />
                      {meta.label}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{s.zip_filename || s.id}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
                      <span><UserIcon className="inline h-3 w-3 mr-0.5" /> Membre : {s.member?.nom || s.member_id?.slice(0, 8) || '—'}</span>
                      <span><UserIcon className="inline h-3 w-3 mr-0.5" /> Cooper : {s.cooper?.nom || s.cooper_user_id?.slice(0, 8) || '—'}</span>
                      <span><Calendar className="inline h-3 w-3 mr-0.5" /> Envoyé : {formatDateFR(s.sent_at)}</span>
                      {s.revoked_at && <span><XCircle className="inline h-3 w-3 mr-0.5" /> Révoqué : {formatDateFR(s.revoked_at)}</span>}
                      <span>📦 {s.included_count || 0} doc(s) — {formatBytes(s.zip_size)}</span>
                      <span><Download className="inline h-3 w-3 mr-0.5" /> {s.access_count} accès</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openShareLogs(s)}
                    data-testid={`rgpd-view-logs-${s.id}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
            <span>{skip + 1}–{Math.min(skip + limit, total)} sur {total}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => { const ns = Math.max(0, skip - limit); setSkip(ns); loadAudit(ns); }}>Précédent</Button>
              <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => { const ns = skip + limit; setSkip(ns); loadAudit(ns); }}>Suivant</Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal détail logs */}
      <Dialog open={!!selectedShare} onOpenChange={(o) => !o && setSelectedShare(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Logs d'accès — {selectedShare?.zip_filename}</DialogTitle>
          </DialogHeader>
          {logsLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-indigo-500" /></div>
          ) : accessLogs.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">Aucun téléchargement enregistré</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {accessLogs.map((log, idx) => (
                <div key={idx} className="border border-slate-200 rounded p-3 text-xs bg-slate-50">
                  <div className="font-semibold text-slate-800">
                    {log.accessed_by_email || log.accessed_by_user_id?.slice(0, 8) || 'Anonyme'}
                  </div>
                  <div className="mt-1 text-slate-600 flex flex-wrap gap-x-4">
                    <span><Calendar className="inline h-3 w-3 mr-0.5" /> {formatDateFR(log.accessed_at)}</span>
                    <span><Globe className="inline h-3 w-3 mr-0.5" /> IP : {log.ip || '—'}</span>
                    {log.anonymized_at && (
                      <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-800 font-semibold">Anonymisé le {formatDateFR(log.anonymized_at)}</Badge>
                    )}
                  </div>
                  {log.user_agent && log.user_agent !== '[anonymized]' && (
                    <div className="mt-1 text-slate-400 text-[10px] truncate">UA : {log.user_agent}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
